import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import LuxButton from "../components/landing/LuxButton";
import { API } from "../App";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) setError("Invalid or missing reset token");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/email/password-reset-confirm`, { token, new_password: formData.password });
      setSuccess(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lux">
      <div className="lux-auth-shell">
        <div className="lux-auth-card" data-testid="reset-password-card">
          <Link to="/" className="flex items-center justify-center mb-7">
            <img src="/logo.png" alt="Fieldz AI" className="h-11 object-contain" />
          </Link>

          {error ? (
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.30)" }}
              >
                <AlertCircle className="w-8 h-8" style={{ color: "#EF4444" }} />
              </div>
              <h1 className="text-[24px] font-bold text-[var(--lux-text)] mb-2" style={{ letterSpacing: "-0.02em" }}>
                Link expired
              </h1>
              <p className="lux-body text-[14px] mb-6">{error}</p>
              <Link to="/forgot-password">
                <LuxButton variant="primary" size="lg" className="w-full" data-testid="reset-request-new-btn">
                  Request New Reset Link
                </LuxButton>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: "rgba(34, 197, 94, 0.15)", border: "1px solid rgba(34, 197, 94, 0.30)" }}
              >
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-[24px] font-bold text-[var(--lux-text)] mb-2" style={{ letterSpacing: "-0.02em" }}>
                Password reset!
              </h1>
              <p className="lux-body text-[14px] mb-6">
                Your password has been reset successfully. Redirecting to login...
              </p>
              <Link to="/login">
                <LuxButton variant="primary" size="lg" className="w-full" data-testid="reset-go-to-login-btn">
                  Go to Login
                </LuxButton>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-7">
                <h1 className="text-[26px] font-bold text-[var(--lux-text)] mb-1.5" style={{ letterSpacing: "-0.02em" }}>
                  Reset password
                </h1>
                <p className="lux-body text-[14px]">Enter your new password below.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="password" className="lux-label">New Password</label>
                  <div className="relative">
                    <Lock className="lux-input-icon" />
                    <input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="lux-input has-icon"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="lux-label">Confirm Password</label>
                  <div className="relative">
                    <Lock className="lux-input-icon" />
                    <input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="lux-input has-icon"
                      required
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <LuxButton type="submit" variant="primary" size="lg" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </LuxButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
